export interface ISpecialtyBid {
    specialtyName: string;
    bidAmountCents: number;
    specialtyCapCents?: number;
    enabled?: boolean;
}

export interface ICreditBalance {
    totalCents: number;
    usedCents: number;
    remainingCents: number;
}

export enum BudgetStatus {
    ACTIVE = 'ACTIVE',
    PAUSED = 'PAUSED',
    BUDGET_EXHAUSTED = 'BUDGET_EXHAUSTED',
    PAYMENT_OVERDUE = 'PAYMENT_OVERDUE',
}

export enum BudgetPeriod {
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
}

export enum ListingStatus {
    PENDING_REVIEW = 'PR', // Pending Review — Uniprofile 待审核（under-review）
    LISTED = 'L', // Listed — 已上架，正常接诊（listed）
    UNLISTED = 'U', // Unlisted — 未上架（not-listed）
    DISQUALIFIED = 'D', // Disqualified — 审核/合规未过，hold（on-hold）
    UNIPROFILE_NEEDED = 'UN', // Uniprofile Needed — 资料缺失，需补全才能上架（not-listed）
    PAUSE_LISTING = 'PL', // Pause Listing — 上架暂停（如欠费），页面可达但不接新患（not-listed）
}

export interface PausePeriod {
    start: string;
    end: string;
}

export interface IAdBudget {
    id?: number;
    providerId?: number;
    budgetUuid?: string;
    budgetPeriod: BudgetPeriod;
    budgetCapCents: number; // 预算上限（分为单位）
    currentPeriodStart?: string; // 2025-01-01T00:00:00
    currentPeriodEnd?: string;
    spentThisPeriodCents?: number;
    remainingBudgetCents?: number;
    status?: BudgetStatus;
    autoRenew?: boolean;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: number;
    updatedBy?: number;
    updatedByName?: string;
    generalBidAmountCents?: number;
    enableAdvancedSettings?: boolean;
    specialtyBids?: ISpecialtyBid[];
    creditBalance?: ICreditBalance;
    pauseTimeRanges?: PausePeriod[];
}

export interface ISpecialtyBidDisplay {
    specialtyName: string;
    bidAmount: number; // 转换为元
    specialtyCap?: number; // 转换为元
    enabled?: boolean;
}

export interface ICreditBalanceDisplay {
    total: number;
    used: number;
    remaining: number;
}

export interface IAdBudgetDisplay {
    id?: number;
    providerId?: number;
    budgetUuid?: string;
    budgetPeriod: BudgetPeriod;
    budgetCap: number; // 转换为元
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    spentThisPeriod?: number; // 转换为元
    remainingBudget?: number; // 转换为元
    status?: BudgetStatus;
    autoRenew?: boolean;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: number;
    updatedBy?: number;
    updatedByName?: string;
    generalBidAmount: number; // 转换为元
    enableAdvancedSettings?: boolean;
    specialtyBids?: ISpecialtyBidDisplay[];
    creditBalance?: ICreditBalanceDisplay;
}

export interface IAdMetricsQuery {
    state?: string;
    specialty?: string;
    week: number;
}

export interface IAdMetricsTrend {
    date?: string; // 日期，格式：YYYY-MM-DD
    bookings?: number;
    adBookings?: number;
    impressions?: number;
    spend?: number; // 当日花费（分为单位）
    auctionParticipants?: number;
}

export interface IAdMetrics {
    totalBookings?: number;
    adBookings?: number;
    totalImpressions?: number;
    totalSpendCents?: number;
    auctionParticipants?: number;
    previousAuctionParticipants?: number;
    previousTotalBookings?: number;
    previousAdBookings?: number;
    previousTotalImpressions?: number;
    trends?: IAdMetricsTrend[];
    pauseTimeRanges?: PausePeriod[];
    joinedAt: string;
}

export interface IAdMetricsTrendDisplay {
    dates?: string[];
    adBookings?: number[];
    bookings?: number[];
    impressionShareRate?: number[]; // 百分比
    pauseTimeRanges?: PausePeriod[];
}
// --- Practice (Group) Types ---

export interface IPracticeOverview {
    listingState: 'OFF' | 'PARTIAL' | 'ALL';
    onProListing: number;
    totalProviders: number;
    organizationId: number;
    poolCapCents: number | null;
    poolSpentCents: number | null;
    poolStatus: BudgetStatus | null;
    createdAt: string | null;
    slugName: string;
    lastModifiedAt?: string | null;
    lastModifiedBy?: string | null;
}

export interface IPracticeMetricsQuery {
    providerId?: number;
    specialty?: string;
    state?: string;
    week: number;
}

export interface IPracticeMetricsSummary {
    totalBookings: number;
    adBookings: number;
    totalImpressions: number;
    totalSpendCents: number;
    auctionParticipants: number;
    previousTotalBookings: number;
    previousAdBookings: number;
    previousTotalImpressions: number;
    previousAuctionParticipants: number;
    totalBookingsRate: number | null;
    totalImpressionsRate: number | null;
    joinedAt: string;
    pauseTimeRanges: PausePeriod[];
    trends: IAdMetricsTrend[];
}

export interface IPracticeAdTrend {
    trends: IAdMetricsTrend[];
    pauseTimeRanges: PausePeriod[];
}

export interface IPracticeByProviderQuery {
    startDate: string;
    endDate: string;
    providerId?: number;
    specialty?: string;
    sortBy?: 'bookings' | 'impressions' | 'spent';
}

export interface IMemberSpecialty {
    specialtyId: number;
    specialtyName: string;
    logo: string;
}

export interface IPracticeByProviderRow {
    providerId: number;
    name: string;
    title: string;
    avatar: string | null;
    onProListing: boolean;
    bidAmountCents: number | null;
    bookings: number;
    impressions: number;
    impressionShare: number;
    spentCents: number;
    budgetStatus?: BudgetStatus | null;
    listingStatus?: ListingStatus | null;
    // Timestamp at which this provider's Pro-Listings were paused due to budget exhaustion.
    budgetExhaustedPausedAt?: string | null;
    services: IMemberSpecialty[];
}

export interface IMemberSpecialtyBidView {
    specialtyName: string;
    bidAmountCents: number;
    specialtyCapCents: number | null;
    enabled?: boolean;
}

export interface IPracticeMember {
    providerId: number;
    enrolled: boolean;
    isOwner: boolean;
    bidAmountCents: number | null;
    capCents: number;
    spentCents: number;
    status: string | null;
    specialtyBids: IMemberSpecialtyBidView[];
}

export interface IPoolSpecialtyBid {
    specialtyName: string;
    bidAmountCents: number;
    specialtyCapCents: number | null;
    enabled: boolean;
    capExhausted?: boolean;
    differenceFromGeneralCents?: number;
}

export interface IPoolBudgetRow {
    budgetPeriod: string;
    budgetCapCents: number;
    spentThisPeriodCents: number;
    status: string;
    advancedSettingsSwitch: boolean;
}

export interface IPracticePoolBudget {
    organizationId: number;
    defaultBidAmountCents: number;
    pool: IPoolBudgetRow | null;
    specialtyBids: IPoolSpecialtyBid[];
    members: IPracticeMember[];
}

export interface IPracticeSaveMemberSpecialtyBid {
    specialtyName: string;
    bidAmountCents: number | null;
    specialtyCapCents: number | null;
    enabled: boolean;
}

export interface IPracticeSaveMember {
    providerId: number;
    enrolled: boolean;
    bidAmountCents: number | null;
    capCents: number | null;
    specialtyBids?: IPracticeSaveMemberSpecialtyBid[];
}

export interface IPracticePoolSaveSpecialtyBid {
    specialtyName: string;
    bidAmountCents: number;
    bidType?: string;
    specialtyCapCents: number | null;
    enabled: boolean;
}

export interface IPracticeSaveRequest {
    poolCapCents: number;
    budgetPeriod: string;
    defaultBidAmountCents: number;
    advancedSettingsSwitch: boolean;
    specialtyBids: IPracticePoolSaveSpecialtyBid[];
    members: IPracticeSaveMember[];
    updateReason?: string;
}

// Working model for the group specialty/provider bid & cap overrides UI.
export interface SpecialtyOverrides {
    specBids: Record<string, number>;
    specCaps: Record<string, string>;
    provBids: Record<string, Record<number, number>>;
    provCaps: Record<string, Record<number, string>>;
    disabledSpecs: Set<string>;
    disabledProvSpecs: Record<string, Set<number>>;
}

// --- End Practice (Group) Types ---

export interface IAdMetricsDisplay {
    totalBookings?: number;
    adBookings?: number;
    totalImpressions?: number;
    totalSpend?: number; // 转换为元
    auctionParticipants?: number;
    previousTotalBookings?: number;
    previousAdBookings?: number;
    previousTotalImpressions?: number;
    previousImpressionShareRate?: number; // 百分比
    impressionShareRate?: number; // 百分比
    bookingsChangeRate?: number; // 百分比
    totalBookingsChangeRate?: number; // 百分比
    impressionsChangeRate?: number; // 百分比
    impressionShareChangeRate?: number; // 百分比
    trends?: IAdMetricsTrend[];
    trendsDisplay?: IAdMetricsTrendDisplay;
    joinedAt?: string;
}
